<script setup lang="ts">

import { type ICondition, ConditionType } from "@domain/Vehicle";
import { useUserSettings } from "@services/UserSettings";
import "./ProgressBar.scss";

const FLASH_CURRENT_FILLED_TICK = true;
const SHOW_CURRENT_INDICATOR_CIRCLE = false;
const SHOW_ALERTED_BORDER_ANIMATION = false;
const SHOW_DUE_INDICATOR_LINE = true;

const model = defineModel<ICondition>( { required: true } );
defineProps<{ isGroup: boolean}>();

const userSettings = useUserSettings();
const isReversed = userSettings.reverseConditionIndicators;
const quantScore = Math.max( Math.ceil( model.value.score * 10 ), 1 );
const score = isReversed ? Math.max( 10 - quantScore, 1 ) : quantScore; 

</script>

<template>

    <!-- PROGRESS BAR -->
    <div class="w-full mt-4 mb-2 lg:mb-0 2xl:scale-[115%] 2xl:mt-6 2xl:mb-4">

        <div class="h-[6rem] 3xl:h-[7rem]">

            <!-- TRACK -->
            <div class="absolute w-full rounded-[10px] shadow-sm dark:shadow-none
                        dark:opacity-50 bg-white dark:bg-gray-900 p-2">

                <!-- PULSE ANIMATION -->
                <div v-if="SHOW_ALERTED_BORDER_ANIMATION"
                     class="condition-alert absolute w-full h-full rounded-lg top-0 left-0 bg-transparent text-transparent"
                     :class="{ 
                         '!text-amber-500': model.isDue && score >= 2,
                         '!text-error-500': model.isCritical || model.isOverdue || ( isReversed == false && score < 2 ) || ( isReversed && score >= 9 ),
                     }">
                </div>

                <!-- EMPTY TICKS -->
                <div class="w-full h-8 flex gap-0.5 dark:gap-1">
                    <div v-for="index in 10"
                         class="bg-gray-100/30 dark:bg-gray-800 w-[10%] h-8"
                         :class="{ 
                             'rounded-l-[3px]': index == 1,
                             'rounded-r-[3px]': index == 10,
                             'opacity-0': model.hasAlert
                         }">
                    </div>
                </div>
            </div>

            <!-- DETAILS -->
            <div class="absolute w-full p-2 rounded-lg">
                <div class="w-full h-8 flex gap-0.5 dark:gap-1">

                    <!-- FILLED TICKS -->
                    <div v-for="index in 10"
                         class="w-[10%] h-8 opacity-0 flex items-center justify-center overflow-hidden"
                         :class="{ 
                             'rounded-l-[4px]': index == 1,
                             'rounded-r-[4px]': index == 10 || ( index == score && model.isOverdue == false ),
                             'opacity-100': index <= score || ( isReversed && model.isOverdue ),
                             'bg-success-500': ( isReversed ? index <= 8 : index > 2 ),
                             'bg-amber-400 dark:bg-amber-500': ( isReversed ? index >= 8 : index <= 2 ),
                             '!bg-error-500/70': model.isOverdue || ( isReversed == false && score <= 1 ) || ( isReversed && score >= 9 ),
                             'indicator-flash-anim': ( FLASH_CURRENT_FILLED_TICK && index == score && model.hasAlert ) || ( model.isOverdue && isReversed == false && index <= 1 ) || ( model.isOverdue && isReversed )
                         }">

                        <!-- SHADOW/HIGHLIGHT DETAILS -->
                        <div class="invisible dark:visible bg-black/[8%] dark:dark:bg-gray-900/[15%] w-full h-1 absolute bottom-0"></div>
                        <div class="invisible dark:visible bg-black/[8%] dark:dark:bg-white/[15%] w-full h-1 absolute top-0"></div>

                    </div>
                    
                    <!-- ALERT MESSAGE -->
                    <div class="absolute top-12 w-full text-center tracking-widest">
                        <div class="3xl:text-lg">
                            <span v-if="model.isDue || model.hasAlert == false">Due </span>
                            <span v-else-if="model.isOverdue">Overdue </span>
                            <span v-if="model.type == ConditionType.Rego">
                                {{ model.labels.dateLabel }}
                            </span>
                            <span v-else>{{ model.getDateRelativeLabel() }}</span>
                        </div>
                        <div v-if="model.labels.odometerRelativeLabel && model.type == ConditionType.Service"
                             class="-mt-0.5 opacity-50 text-xs 3xl:text-sm">
                            or {{ model.labels.odometerRelativeLabel }}
                        </div>
                    </div>
                        
                    <!-- INDICATOR LINE -->
                    <div v-if="SHOW_DUE_INDICATOR_LINE"
                         class="absolute flex items-center h-full w-full"
                         :style="{ 
                            left: ( isReversed ? 91 : 10 ) + '%',
                            width: ( isReversed ? 91 : 90 ) + '%'
                        }">
                         
                        <div class="absolute h-12 bg-black dark:bg-white rounded-md
                                    w-[6px] border-[2px] border-white -left-[6px]
                                    dark:w-[10px] dark:border-[4px] dark:border-gray-850">
                        </div>

                    </div>

                    <!-- STATE LABELS -->
                    <div v-if="model.hasAlert"
                         class="indicator-flash-anim
                                rounded-[2px] absolute
                                h-8 whitespace-nowrap justify-start
                                flex items-center tracking-widest uppercase"
                         :style="{ 
                            left: ( isReversed ? 0 : 10 ) + '%',
                            width: ( isReversed ? 90 : ( 100 - ( score * 10 ) ) ) + '%'
                         }">

                        <div class="px-3 font-bold shadow-gray-900"
                             style="text-shadow: 0 1px 3px var(--tw-shadow-color), 0 -1px 2px var(--tw-shadow-color)"
                             :class="{ 
                                 'text-amber-600 dark:text-white': model.isDue && score >= 2,
                                 'text-rose-600 dark:text-white': ( model.isOverdue || score < 2 ) && isReversed,
                                 'text-rose-600 dark:text-rose-500': ( model.isOverdue || score < 2 ) && isReversed == false,
                                 '!pl-3': isReversed
                             }">
                            {{ model.labels.stateLabel }}
                        </div>

                    </div>

                    <!-- CURRENT POSITION CIRCLE INDICATOR -->
                    <div v-if="SHOW_CURRENT_INDICATOR_CIRCLE"
                         class="w-full h-8 flex gap-0.5 dark:gap-1 absolute">
                        <div v-for="index in 10"
                             class="w-[10%] h-8 flex items-center justify-center">
                             <div v-if="( index == score && model.isOverdue == false ) || ( model.isOverdue && isReversed && index == 10 ) || ( model.isOverdue && isReversed == false && index == 1 )"
                                  class="w-2 h-2 rounded-full bg-white absolute">
                            </div>
                        </div>
                    </div>

                </div>
           
            </div>
        </div>

    </div>

</template>
